import { Box, Grid, styled, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { setMortgageAction, setNewMortgageAction } from "../../../contexts/mortgage/Actions";
import { useMortgageDispatch, useMortgageState } from "../../../contexts/mortgage/Reducer";
import { setStepAction } from "../../../contexts/steps/Actions";
import { useStepDispatch } from "../../../contexts/steps/Reducer";
import { AnalyticsActivity, AppComponent } from "../../../services/event-service/EventService";
import { useEventService } from "../../../services/event-service/EventServiceContext";
import { DateFormat, formatDate } from "../../../utils/DateUtils";
import { formatAmount } from "../../../utils/NumberUtils";
import AddressInfo from "../../address-info/AddressInfo";
import ErrorMessage from "../../feedback/ErrorMessage";
import MortgageStepper from "../../mortgage-stepper/MortgageStepper";
import { BackButton, ButtonBox, ForwardButton } from "../2-summary/SummaryScreen";
import ConfirmationDialog from "../popups/ConfirmationDialog";
import ConfigurationSummary from "./ConfigurationSummary";
import ConfigurationWarning from "./mortgage-configurator/ConfigurationWarning";
import Configurator from "./mortgage-configurator/Configurator";

const ConfigurationInfoText = styled(Box)(({ theme }) => ({
    marginBottom: "46px",
    [theme.breakpoints.up("lg")]: {
        marginBottom: "32px",
    },
}));

const StepperWrapper = styled(Box)(({ theme }) => ({
    [theme.breakpoints.up("lg")]: {
        marginBottom: "48px",
    },
}));
/**
 * A first screen of the mortgage extension wizard which allows the customer to configure the mortgage he wants to extend.
 */
const ConfigurationScreen = () => {
    const { t } = useTranslation();

    const stepDispatch = useStepDispatch();
    const { mortgage, newMortgage } = useMortgageState();
    const mortgageDispatch = useMortgageDispatch();
    const eventService = useEventService();

    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

    if (mortgage === undefined || newMortgage === undefined) {
        return <ErrorMessage />;
    }

    const cancelMortgageExtension = () => {
        eventService.sendAnalyticsEvent(AnalyticsActivity.LEAVE_PROCESS, AppComponent.CONFIGURATION_SCREEN);

        stepDispatch(setStepAction(0));
        mortgageDispatch(setNewMortgageAction(undefined));
        mortgageDispatch(setMortgageAction(undefined));
    };

    const continueToSummary = () => {
        eventService.sendAnalyticsEvent(AnalyticsActivity.NAVIGATE_TO_SUMMARY, AppComponent.CONFIGURATION_SCREEN);
        stepDispatch(setStepAction(2));
    };

    return (
        <Grid container data-cy={"configuration-screen"}>
            <Grid item sm={12} md={12} lg={12}>
                <StepperWrapper>
                    <MortgageStepper />
                </StepperWrapper>
            </Grid>
            <Grid item sm={0} md={1} lg={1}></Grid>
            <Grid item sm={12} md={10} lg={10}>
                <AddressInfo selectedMortgage={mortgage} />
                <ConfigurationInfoText data-cy={"configuration-info-text"}>
                    <Typography variant="body1">
                        {t("ConfigurationScreen.Your mortgage expires on", {
                            amount: formatAmount(newMortgage.amount),
                            currency: mortgage.currency,
                            expirationDate: formatDate(mortgage.expirationDate, DateFormat.DATE),
                        })}
                    </Typography>
                </ConfigurationInfoText>

                <Configurator />
                {newMortgage && newMortgage.repayment > 0 && <ConfigurationWarning />}
                <ConfigurationSummary />
                <ButtonBox>
                    <BackButton onClick={() => setCancelDialogOpen(true)} data-cy={"cancel-button"}>
                        {t("Actions.Cancel")}
                    </BackButton>
                    <ForwardButton
                        variant="contained"
                        onClick={() => continueToSummary()}
                        disabled={Number(newMortgage.duration) === 0}
                        data-cy={"continue-button"}>
                        {t("Actions.Continue")}
                    </ForwardButton>
                </ButtonBox>
            </Grid>
            <Grid item sm={0} md={1} lg={1}></Grid>

            <ConfirmationDialog
                open={cancelDialogOpen}
                title={"Actions.Leave the process?"}
                message={"Actions.Your entries will be lost"}
                confirmButtonLabel={"Actions.Leave process"}
                cancelButtonLabel={"Actions.Back"}
                cancelAction={() => setCancelDialogOpen(false)}
                confirmAction={cancelMortgageExtension}
            />
        </Grid>
    );
};
export default ConfigurationScreen;
